import { IAttribute, IModel, Collection } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { ScriptFlowActivity } from './script-flow.activity';
import { ParameterConfig } from 'app/model/pipeline/parameter-config';
import { createParameterConfigurationValidator } from 'app/model/adapters/linkedservices/parameter-config.util';
import { AzureDataLakeAnalyticsLinkedService } from 'app/model/linkedservice/azure-data-lake-analytics.linkedservice';
import { ReferenceObjectParameters } from 'app/model/pipeline/reference-object-parameters';
import { AzureDataLakeStoreLinkedService } from 'app/model/linkedservice/azure-data-lake-store.linkedservice';
import { AdlsCompileResultDataset } from 'app/interfaces/IScriptCompilation';
import { AzureDataLakeStoreCosmosStructuredStreamLinkedService } from '../linkedservice/azure-data-lake-store-cosmos-structured-stream.linkedservice';
import { format } from 'app/shared/utils/string.util';
import { Factory, IValidationContext, IViolation, ModelConstants } from 'shell-models-lib';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ModelService } from '../services/model.service';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';

export type NodeSettingType =
    | 'Default'
    | 'Token allocation'
    | 'Percentage allocation'
    | 'Eco Token allocation'
    | 'Eco Percentage allocation';
	
export type NewNodeSettingType =
    | 'Default'
    | 'Custom';

@Metamodel.model({
    name: ModelConstants.scopeActivity,
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.Scope),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.Scope)
    ],
    referable: true,
    constraints: {
        name: {
            required: true
        }
    },
    validator: ScopeActivity._validate
})
export class ScopeActivity extends ScriptFlowActivity {
    public static readonly MODEL: IModel;
    public static readonly FOLDER_PATH: IAttribute;
    public static readonly FILE_NAME: IAttribute;
    public static readonly ALLOCATION_LEVEL: IAttribute;
    public static readonly ALLOCATION_VALUE: IAttribute;
	public static readonly TOKEN_ALLOCATION_TYPE: IAttribute;
    public static readonly NORMAL_TOKENS_VALUE: IAttribute;
    public static readonly NORMAL_TOKENS_PERCENT_VALUE: IAttribute;
    public static readonly ECO_TOKENS_VALUE: IAttribute;
    public static readonly ECO_TOKENS_PERCENT_VALUE: IAttribute;
    public static readonly EMAIL_RECIPIENTS: IAttribute;
    public static readonly JOB_NAME: IAttribute;
    public static readonly JOB_OWNER: IAttribute;
    public static readonly SCOPE_SCRIPT_INCLUSION_SET: IAttribute;
    public static readonly PRIORITY: IAttribute;
    public static readonly RUNTIME_VERSION: IAttribute;
    public static readonly NEBULA_ARGUMENTS: IAttribute;
    public static readonly SCOPE_PARAMETERS: IAttribute;
    public static readonly TEST_ISOLATION: IAttribute;

    @Metamodel.attribute({
        display: stringResources.FolderPath_Label,
        association: 'FOLDER_PATH',
        templatable: true,
        constraint: {
            required: true
        }
    })
    public folderPath?: string;

    @Metamodel.attribute({
        display: stringResources.FileNameLabel,
        association: 'FILE_NAME',
        templatable: true,
        constraint: {
            required: true
        }
    })
    public fileName?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_Priority,
        templatable: true,
        association: 'PRIORITY'
    })
    public priority?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_RuntimeVersion,
        templatable: true,
        association: 'RUNTIME_VERSION'
    })
    public runtimeVersion?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_NebulaArguments,
        templatable: true,
        association: 'NEBULA_ARGUMENTS'
    })
    public nebulaArguments?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_EmailRecipients,
        association: 'EMAIL_RECIPIENTS',
        templatable: true
    })
    public emailRecipients?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_JobOwner,
        association: 'JOB_OWNER',
        templatable: true
    })
    public jobOwner?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_JobName,
        association: 'JOB_NAME',
        templatable: true
    })
    public jobName?: string;

    @Metamodel.attribute({
        display: '',
        association: 'ALLOCATION_LEVEL',
        templatable: true
    })
    public allocationLevel?: NodeSettingType;

    @Metamodel.attribute({
        display: '',
        association: 'ALLOCATION_VALUE',
        templatable: true
    })
    public allocationValue?: string;

    @Metamodel.attribute({
        display: '',
        association: 'TOKEN_ALLOCATION_TYPE',
        templatable: true
    })
    public tokenAllocationType: NewNodeSettingType = 'Default';

    @Metamodel.attribute({
        display: '',
        association: 'NORMAL_TOKENS_VALUE',
        templatable: true
    })
    public normalTokensValue?: string;

    @Metamodel.attribute({
        display: '',
        association: 'NORMAL_TOKENS_PERCENT_VALUE',
        templatable: true
    })
    public set normalTokensPercentValue(value: string | undefined) {
        this._normalTokensPercentValue = this._capPercentageValue(value);
    }
    public get normalTokensPercentValue(): string | undefined {
        return this._normalTokensPercentValue;
    }
    private _normalTokensPercentValue?: string;

    @Metamodel.attribute({
        display: '',
        association: 'ECO_TOKENS_VALUE',
        templatable: true
    })
    public ecoTokensValue?: string;

    @Metamodel.attribute({
        display: '',
        association: 'ECO_TOKENS_PERCENT_VALUE',
        templatable: true
    })
    public set ecoTokensPercentValue(value: string | undefined) {
        this._ecoTokensPercentValue = this._capPercentageValue(value);
    }
    public get ecoTokensPercentValue(): string | undefined {
        return this._ecoTokensPercentValue;
    }
    private _ecoTokensPercentValue?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_ScopeScriptInclusionSet,
        association: 'SCOPE_SCRIPT_INCLUSION_SET',
        templatable: true
    })
    public scopeScriptInclusionSet?: string;

    @Metamodel.attribute({
        templatable: true,
        display: stringResources.Parameters,
        association: 'SCOPE_PARAMETERS',
        constraint: {
            validator: createParameterConfigurationValidator()
        }
    })
    public parameters?: ParameterConfig[];

    @Metamodel.attribute({
        display: '',
        association: 'TEST_ISOLATION',
        templatable: true
    })
    public testIsolation?: boolean;

    @Metamodel.attribute({
        display: __IS_FABRIC_DPE__
            ? stringResources.ModelName_ADLAAccount_Trident
            : stringResources.ModelName_ADLAAccount,
        constraint: {
            required: true
        },
        reference: true,
        type: AzureDataLakeAnalyticsLinkedService.MODEL
    })
    public linkedService?: AzureDataLakeAnalyticsLinkedService;


    // --- Most of the attributes below here are obsolete (from disabled Scope Compilation process)
    @Metamodel.attribute({
        display: stringResources.ModelName_OutputDatasets,
        reference: true,
        collection: Collection.Array
    })
    public outputDatasets?: AdlsCompileResultDataset[] = [];

    @Metamodel.attribute({
        collection: Collection.Array,
        constraint: {
            required: false
        }
    })
    public outputDatasetsParameters: ReferenceObjectParameters[] = [];

    @Metamodel.attribute({
        display: stringResources.ModelName_LastCompiledTime
    })
    public lastCompiledTime: Date;

    @Metamodel.attribute({
        display: stringResources.ModelName_OutputAdlsLinkedService,
        reference: true
    })
    public outputAdlsLinkedService?: AzureDataLakeStoreLinkedService;

    @Metamodel.attribute({
        display: stringResources.ModelName_OutputAdlsCosmosStructuredStreamLinkedService,
        reference: true
    })
    public outputAdlsCosmosStructuredStreamLinkedService?: AzureDataLakeStoreCosmosStructuredStreamLinkedService;

    @Metamodel.attribute({
        display: stringResources.ModelName_DateTimeParamName
    })
    public dateTimeParamName?: string;

    constructor(name?: string, description?: string) {
        super(name, description);
        this.activityType = 'ExecutionActivity';
    }

    public getFullScriptPath(): string {
        return [this.folderPath, this.fileName].filter((value) => value).join('/');
    }

    private _capPercentageValue(value: string | undefined): string | undefined {
        if (value === undefined || value === null || value === '') {
            return value;
        }
        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
            return value;
        }
        if (numValue > 100) {
            return '100';
        }
        return value;
    }

    /* eslint-disable @typescript-eslint/member-ordering */
    private static async _validate(owner: {}, context: IValidationContext): Promise<IViolation[] | undefined> {
        const ecSvc = await getServiceInstance<ExposureControlService>(context.contextData, 'ExposureControlService');
        if (ecSvc.enableScopeActivityRestriction) {
            if (!__IS_FABRIC_DPE__) {
                const factory = (await getServiceInstance<ModelService>(context.contextData, 'ModelService')).factory;
                if (!Factory.hasScopeSupportEnabled(factory)) {
                    return [
                        {
                            message: () => stringResources.ScopeActivity_FactoryRestriction
                        }
                    ];
                }
            }
        }

        // Validate mixed mode token allocation rules
        const activity = owner as ScopeActivity;
        if (activity.tokenAllocationType === 'Custom') {
            const tokenAllocationError = ScopeActivity._validateTokenAllocation(activity);
            if (tokenAllocationError) {
                return [tokenAllocationError];
            }
        }

        return undefined;
    }

    private static _validateTokenAllocation(activity: ScopeActivity): IViolation | null {
        const normalTokens = ScopeActivity._parseNumericValue(activity.normalTokensValue);
        const normalTokensPercent = ScopeActivity._parseNumericValue(activity.normalTokensPercentValue);
        const ecoTokens = ScopeActivity._parseNumericValue(activity.ecoTokensValue);
        const ecoTokensPercent = ScopeActivity._parseNumericValue(activity.ecoTokensPercentValue);

        const fields = [normalTokens, normalTokensPercent, ecoTokens, ecoTokensPercent];
        const nonNullFields = fields.filter((f) => f !== null);

        // Rule 1: Max 2 fields can have a non-zero or non-undefined input
        if (nonNullFields.length > 2) {
            return {
                message: () => stringResources.ScopeActivity_TokenAllocation_MaxTwoFieldsError
            };
        }

        // If we have exactly 2 fields, check the specific combination rules
        if (nonNullFields.length === 2) {
            // Rule 2: You cannot set both fields for the same type (multi mode settings)
            const hasNormalTokens = normalTokens !== null;
            const hasEcoTokens = ecoTokens !== null;
            const hasNormalPercent = normalTokensPercent !== null;
            const hasEcoPercent = ecoTokensPercent !== null;
            
            if ((hasNormalPercent && hasNormalTokens) || (hasEcoPercent && hasEcoTokens)) {
                return {
                    message: () => stringResources.ScopeActivity_TokenAllocation_MultiAllocationSameTokenError
                }
            }
        }

        return null;
    }

    private static _parseNumericValue(value: string | undefined): number | null {
        if (value === undefined || value === null || value === '') {
            return null;
        }
        const parsed = parseFloat(value);
        return isNaN(parsed) || parsed === 0 ? null : parsed;
    }
}
